import { isEqual, isNil } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { type CompanyContextDataFragment } from '../../../api/fragments/CompanyContextData';
import { DealerListDataFragment } from '../../../api/fragments/DealerListData';
import { useListCompaniesForSelectionQuery } from '../../../api/queries/listCompaniesForSelection';
import { useListDealersQuery } from '../../../api/queries/listDealers';
import useTranslatedString from '../../../utilities/useTranslatedString';
import LoadingElement from '../../LoadingElement';
import { useAccount } from '../AccountContextManager';
import { DealerContext, type DealerContextData, type DealerOption } from './Context';

type DealersState = {
    id: string;
    isSingle: boolean;
    isMultiple: boolean;
    // visible :: showing up the option in the dealer dropdown
    visible: boolean;
};

export type DealerContextManagerProps = {
    company?: CompanyContextDataFragment | null;
    children: React.ReactNode;
};

const DEFAULT_STORAGE_KEY = 'dealerIds';

const getDealersFromStorage = (storageKey: string, fallback: DealersState[] = []): DealersState[] | undefined => {
    if (typeof window === 'undefined') {
        return fallback;
    }

    const result = window.sessionStorage.getItem(storageKey);
    if (!result) {
        return fallback;
    }

    return JSON.parse(result);
};

export const DealerContextManager = ({ children, company }: DealerContextManagerProps) => {
    const translate = useTranslatedString();

    const account = useAccount(true);

    const { data: dataCompanies, refetch: refetchCompanyList } = useListCompaniesForSelectionQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: null,
        },
        skip: !account,
    });

    const {
        data: dataDealers,
        loading,
        refetch: refetchDealerList,
    } = useListDealersQuery({
        variables: {
            filter: {
                companyId: company?.id,
                productionOnly: !account,
            },
        },
    });

    const [dealerOptions, setDealerOptions] = useState<DealerOption[]>([]);
    const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);

    // the default dealer
    const defaultDealers = useMemo(
        () =>
            dealerOptions.map((item, index) => ({
                id: item.value,
                isSingle: index === 0,
                isMultiple: true,
                visible: true,
            })),
        [dealerOptions]
    );

    // setting up the dealers when initializing without upserting the new dealer from FE
    useEffect(() => {
        if (dataDealers?.page) {
            const mappedDealerOptions: DealerOption[] = [];
            dataDealers?.page.items.forEach((dealer, index) => {
                mappedDealerOptions.push({
                    label: translate(dealer.legalName),
                    value: dealer.id,
                });
            });

            setDealerOptions(mappedDealerOptions.toSorted((a, b) => a.label.localeCompare(b.label)));
        }
    }, [dataDealers?.page, translate]);

    const storageName = useMemo(
        () => (account ? `${DEFAULT_STORAGE_KEY}-${account?.id}` : DEFAULT_STORAGE_KEY),
        [account]
    );

    // Selected dealer ids the first dealer available will be used in portal
    // Not sure if it's too big if I combine available dealers in session storage
    // So just put the id and single / multiple state
    const [dealers, setDealers] = useSessionStorage<DealersState[] | null>(
        storageName,
        getDealersFromStorage(storageName, defaultDealers)
    );

    useEffect(() => {
        if (!company && dataCompanies?.list.count > 0) {
            refetchDealerList();
        }
    }, [company, dataCompanies?.list.count, refetchDealerList]);

    useEffect(() => {
        // On Company change, change all value in dealers
        // and make the first dealer activate in portal
        const newDealerIds = defaultDealers?.map(dealer => dealer.id);
        const currentDealerIds = dealers?.map(dealer => dealer.id);
        if (defaultDealers?.length > 0 && !isEqual(newDealerIds, currentDealerIds)) {
            // only show the option when dealer is visible
            setDealers(defaultDealers);
        }
    }, [dealers, defaultDealers, setDealers]);
    useEffect(() => {
        if (account?.id && !isNil(window)) {
            window.sessionStorage.removeItem(DEFAULT_STORAGE_KEY);

            // Refetch list companies again
            if (isNil(company) && isNil(dataCompanies)) {
                refetchCompanyList();
            }
        }
    }, [account, company, dataCompanies, refetchCompanyList]);

    const setAllVisible = useCallback(() => {
        const newDealer = dealers.map(dealer => ({ ...dealer, visible: true }));
        setDealers(newDealer);
    }, [dealers, setDealers]);

    const setVisible = useCallback(
        (dealerIds: string[], visible: boolean) => {
            const existedDealer = dealers.map(dealer => {
                if (dealerIds.includes(dealer.id)) {
                    return { ...dealer, visible };
                }

                return dealer;
            });

            setDealers(existedDealer);
        },
        [dealers, setDealers]
    );

    const dealerIdsByCompanyId = useCallback(
        (companyId: string) =>
            dataDealers.page.items.filter(dealer => dealer.company.id === companyId).map(dealer => dealer.id),
        [dataDealers]
    );

    // Provide in context for the states, actions and options
    // For further actions will be describe in hooks below
    // use `useSingleDealerId` or `useMultipleDealerIds` accordingly
    const context = useMemo(
        (): DealerContextData => ({
            setDealers,
            dealers,
            dealerOptions,
            setDealerOptions,
            displayDropdown,
            setDisplayDropdown,
            dealersFromApi: dataDealers?.page.items || [],
            setVisible,
            setAllVisible,
            dealerIdsByCompanyId,
        }),
        [dataDealers?.page.items, dealerOptions, dealers, displayDropdown, setAllVisible, setDealers, setVisible]
    );

    if (loading) {
        // we should wait for data
        return <LoadingElement />;
    }

    return <DealerContext.Provider value={context}>{children}</DealerContext.Provider>;
};
